import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { convertUnits, getUnitType } from '../../utils/unitConversions';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  CircularProgress,
  Alert,
  Stack,
  useTheme,
  alpha,
  Link as MuiLink,
  Chip,
  Fade,
  Card,
  CardContent,
  Grid,
  TablePagination,
  LinearProgress,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LocalDrink as LocalDrinkIcon,
  Home as HomeIcon,
  LocalShipping as LocalShippingIcon,
  ArrowBack as ArrowBackIcon,
  Factory as FactoryIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { BatchTabs } from './components';

// Date utility functions to avoid timezone issues
const formatDateForDisplay = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    // Parse as local date to avoid timezone shifts
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return 'N/A';
  }
};

const BatchingPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canAccessPage, canEditPage, canDeletePage, canCreateProductionBatches, canPermanentlyDeleteProductionBatches } = usePermissions();

  const [batches, setBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [dateFilter, setDateFilter] = useState({
    type: 'all', // 'all', 'range', 'specific', 'expiration'
    startDate: '',
    endDate: '',
    specificDate: '',
    expirationFilter: 'all' // 'all', 'expired', 'expiring_soon', 'not_expired'
  });
  
  // Product filter dialog state
  const [productFilterDialogOpen, setProductFilterDialogOpen] = useState(false);
  const [productFilterSearchTerm, setProductFilterSearchTerm] = useState('');
  const [dialogSelectedProductFilter, setDialogSelectedProductFilter] = useState(null);
  const [productSelectionMade, setProductSelectionMade] = useState(false);
  
  // Date filter dialog state
  const [dateFilterDialogOpen, setDateFilterDialogOpen] = useState(false);
  const [tempDateFilter, setTempDateFilter] = useState({
    type: 'all',
    startDate: '',
    endDate: '',
    specificDate: '',
    expirationFilter: 'all'
  });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // View mode state
  const [viewMode, setViewMode] = useState('list');
  const [currentBatch, setCurrentBatch] = useState(null);
  const [formulations, setFormulations] = useState([]);
  const [products, setProducts] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [user, setUser] = useState(null);
  const [batchTimeFormat, setBatchTimeFormat] = useState('12hour');
  
  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Create a ref for BatchTabs component
  const batchTabsRef = useRef({ openEditDialog: () => {} });

  const [formulationIngredients, setFormulationIngredients] = useState({});
  const [batchCosts, setBatchCosts] = useState({});

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchBatches(),
        fetchFormulations(),
        fetchProducts(),
        fetchRawMaterials(),
        fetchUser(),
        fetchCompanyData()
      ]);
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      
      // Load user profile for time format preference
      if (user?.id) {
        const { data, error } = await supabase
          .from('profiles')
          .select('batch_time_format_preference')
          .eq('id', user.id)
          .single();
          
        if (data && !error) {
          setBatchTimeFormat(data.batch_time_format_preference || '12hour');
        }
      }
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  };
 
  const fetchCompanyData = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      
      if (error) throw error;
      setCompanyName(data?.name || '');
    } catch (err) {
      console.error('Error fetching company data:', err);
    }
  };

  const fetchBatches = async () => {
    try {
      const { data, error } = await supabase
        .from('production_batches')
        .select(`
          *,
          product:product_id(id, name, sku),
          formulation:formulation_id(id, name, version)
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setBatches(data || []);
      setFilteredBatches(data || []);
    } catch (err) {
      console.error('Error fetching batches:', err);
      throw err;
    }
  };

  const fetchFormulations = async () => {
    try {
      const { data, error } = await supabase
        .from('formulations')
        .select('*')
        .eq('company_id', companyId)
        .eq('status', 'approved')
        .order('name');
      
      if (error) throw error;
      setFormulations(data || []);
    } catch (err) {
      console.error('Error fetching formulations:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('company_id', companyId)
        .order('name');
      
      if (error) throw error;
      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchRawMaterials = async () => {
    try {
      const { data, error } = await supabase
        .from('raw_materials')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      setRawMaterials(data || []);
    } catch (err) {
      console.error('Error fetching raw materials:', err);
    }
  };

  const applyFilters = () => {
    let result = [...batches];
    
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(batch => 
        (batch.batch_number && batch.batch_number.toLowerCase().includes(lowercasedSearch)) ||
        (batch.product?.name && batch.product.name.toLowerCase().includes(lowercasedSearch)) ||
        (batch.formulation?.name && batch.formulation.name.toLowerCase().includes(lowercasedSearch)) ||
        (batch.status && batch.status.toLowerCase().includes(lowercasedSearch))
      );
    }
    
    if (selectedProduct) {
      result = result.filter(batch => batch.product_id === selectedProduct);
    }
    
    // Date filtering
    if (dateFilter.type !== 'all') {
      const today = new Date();
      
      result = result.filter(batch => {
        switch (dateFilter.type) {
          case 'range':
            if (dateFilter.startDate && dateFilter.endDate) {
              const mfgDate = new Date(batch.manufacture_date);
              const startDate = new Date(dateFilter.startDate);
              const endDate = new Date(dateFilter.endDate);
              return mfgDate >= startDate && mfgDate <= endDate;
            }
            return true;
          
          case 'specific':
            if (dateFilter.specificDate) {
              const mfgDate = new Date(batch.manufacture_date);
              const specificDate = new Date(dateFilter.specificDate);
              return mfgDate.toDateString() === specificDate.toDateString();
            }
            return true;
          
          case 'expiration':
            if (dateFilter.expirationFilter !== 'all' && batch.expiration_date) {
              const expDate = new Date(batch.expiration_date);
              const thirtyDaysFromNow = new Date();
              thirtyDaysFromNow.setDate(today.getDate() + 30);
              
              switch (dateFilter.expirationFilter) {
                case 'expired':
                  return expDate < today;
                case 'expiring_soon':
                  return expDate >= today && expDate <= thirtyDaysFromNow;
                case 'expiring_range':
                  if (dateFilter.startDate && dateFilter.endDate) {
                    const startDate = new Date(dateFilter.startDate);
                    const endDate = new Date(dateFilter.endDate);
                    return expDate >= startDate && expDate <= endDate;
                  }
                  return true;
                case 'expiring_specific':
                  if (dateFilter.specificDate) {
                    const specificDate = new Date(dateFilter.specificDate);
                    return expDate.toDateString() === specificDate.toDateString();
                  }
                  return true;
                case 'not_expired':
                  return expDate > today;
                default:
                  return true;
              }
            }
            return dateFilter.expirationFilter === 'all';
          
          default:
            return true;
        }
      });
    }
    
    setFilteredBatches(result);
    setPage(0); // Reset to first page when filters change
  };

  useEffect(() => {
    fetchInitialData();
  }, [companyId]);

  const fetchBatchCosts = React.useCallback(async () => {
    try {
      if (batches.length === 0) return;
      
      const batchIds = batches.map(b => b.id);
      const { data, error } = await supabase
        .from('batch_costs')
        .select('batch_id, total_cost, total_production_batch_cost')
        .in('batch_id', batchIds);
      
      if (error && error.code !== 'PGRST116') throw error;
      
      const costsMap = {};
      (data || []).forEach(cost => {
        costsMap[cost.batch_id] = cost.total_production_batch_cost || cost.total_cost;
      });
      setBatchCosts(costsMap);
    } catch (err) {
      console.error('Error fetching batch costs:', err);
    }
  }, [batches]);

  useEffect(() => {
    if (batches.length > 0) {
      applyFilters();
    }
  }, [batches, searchTerm, selectedProduct, dateFilter]);

  useEffect(() => {
    fetchBatchCosts();
  }, [fetchBatchCosts]);



  // Permission check - user needs access to production batches
  if (!canAccessPage('production_batches')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <LocalDrinkIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Batch Production Access Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Batches. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
          </Card>
        </Box>
      </Container>
    );
  }

  const fetchBatchDetails = async (batchId) => {
    try {
      setLoading(true);
      
      const { data: batchData, error: batchError } = await supabase
        .from('production_batches')
        .select(`
          *,
          product:product_id(id, name, sku),
          formulation:formulation_id(id, name, version)
        `)
        .eq('id', batchId)
        .single();
      
      if (batchError) throw batchError;
      
      setCurrentBatch(batchData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching batch details:', error);
      setError('Error loading batch details: ' + error.message);
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenMenu = (event, batch) => {
    setSelectedBatch(batch);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteBatch = async () => {
    if (!selectedBatch) return;
    
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('production_batches')
        .delete()
        .eq('id', selectedBatch.id);
      
      if (error) throw error;
      
      // Update both batches and filteredBatches immediately
      const updatedBatches = batches.filter(b => b.id !== selectedBatch.id);
      setBatches(updatedBatches);
      setFilteredBatches(updatedBatches.filter(batch => {
        if (!searchTerm) return true;
        const lowercasedSearch = searchTerm.toLowerCase();
        return (
          (batch.batch_number && batch.batch_number.toLowerCase().includes(lowercasedSearch)) ||
          (batch.product?.name && batch.product.name.toLowerCase().includes(lowercasedSearch)) ||
          (batch.formulation?.name && batch.formulation.name.toLowerCase().includes(lowercasedSearch)) ||
          (batch.status && batch.status.toLowerCase().includes(lowercasedSearch))
        );
      }));
      
      if (currentBatch && currentBatch.id === selectedBatch.id) {
        handleBackToList();
      }
      
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting batch:', err);
      setError(`Failed to delete batch: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleEditBatch = () => {
    if (!selectedBatch) return;
    handleViewDetails(selectedBatch);
    handleCloseMenu();
  };

  const handleViewDetails = async (batch) => {
    setViewMode('detail');
    await fetchBatchDetails(batch.id);
  };

  const handleBackToList = () => {
    setViewMode('list');
    setCurrentBatch(null);
  };

  const handleCreateNew = () => {
    const newBatch = {
      batch_number: '',
      product_id: '',
      formulation_id: '',
      planned_quantity: 100,
      planned_unit_of_measure: 'g',
      manufacture_date: new Date().toISOString().split('T')[0],
      status: 'draft',
      priority: 'normal',
      company_id: companyId
    };
    
    setCurrentBatch(newBatch);
    setViewMode('detail');
    
    setTimeout(() => {
      if (batchTabsRef.current) {
        batchTabsRef.current.openEditDialog(true);
      }
    }, 100);
  };

  const refreshAllData = async (batchIdOrObject = null) => {
    try {
      await fetchBatches();
      
      // Handle both string ID and batch object
      let batchId = null;
      if (batchIdOrObject) {
        if (typeof batchIdOrObject === 'string') {
          batchId = batchIdOrObject;
        } else if (typeof batchIdOrObject === 'object' && batchIdOrObject.id) {
          batchId = String(batchIdOrObject.id);
        }
      }
      
      if (batchId) {
        await fetchBatchDetails(batchId);
      } else if (currentBatch && currentBatch.id) {
        await fetchBatchDetails(currentBatch.id);
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    if (severity === 'error') {
      setError(message);
    }
  };



  const getStatusColor = (status) => {
    const statusColors = {
      draft: 'default',
      pending_approval: 'warning',
      approved: 'info',
      scheduled: 'primary',
      in_production: 'secondary',
      quality_testing: 'warning',
      completed: 'success',
      on_hold: 'warning',
      cancelled: 'error',
      failed: 'error'
    };
    return statusColors[status] || 'default';
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return 'N/A';
    return `$${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const fetchFormulationIngredients = async (formulationId) => {
    if (!formulationId || formulationIngredients[formulationId]) {
      return formulationIngredients[formulationId] || [];
    }

    try {
      const { data, error } = await supabase
        .from('formulation_ingredients_new')
        .select(`
          *,
          raw_materials:ingredient_id (
            id, name, cas_number, sku, category, 
            material_type, primary_unit_measure, color, form,
            density_g_ml, primary_unit_type, primary_unit_quantity
          )
        `)
        .eq('formulation_id', formulationId)
        .order('sequence');

      if (error) throw error;
      
      const ingredients = data || [];
      setFormulationIngredients(prev => ({
        ...prev,
        [formulationId]: ingredients
      }));
      
      return ingredients;
    } catch (error) {
      console.error('Error loading formulation ingredients:', error);
      return [];
    }
  };

  // Product filter dialog functions
  const getFilteredProductsForFilter = () => {
    if (!productFilterSearchTerm) return products;
    return products.filter(product =>
      product.name.toLowerCase().includes(productFilterSearchTerm.toLowerCase()) ||
      (product.sku && product.sku.toLowerCase().includes(productFilterSearchTerm.toLowerCase()))
    );
  };

  const handleOpenProductFilterDialog = () => {
    setProductFilterDialogOpen(true);
    setProductFilterSearchTerm('');
    setDialogSelectedProductFilter(null);
    setProductSelectionMade(false);
  };

  const handleCloseProductFilterDialog = () => {
    setProductFilterDialogOpen(false);
    setProductFilterSearchTerm('');
    setDialogSelectedProductFilter(null);
    setProductSelectionMade(false);
  };

  const handleDialogProductFilterSelect = (product) => {
    setDialogSelectedProductFilter(product);
    setProductSelectionMade(true);
  };

  const handleConfirmProductFilterSelection = () => {
    if (dialogSelectedProductFilter === 'all') {
      setSelectedProduct('');
    } else if (dialogSelectedProductFilter) {
      setSelectedProduct(dialogSelectedProductFilter.id);
    } else {
      setSelectedProduct('');
    }
    setProductFilterDialogOpen(false);
    setProductFilterSearchTerm('');
    setDialogSelectedProductFilter(null);
    setProductSelectionMade(false);
  };

  const getSelectedProductName = () => {
    if (!selectedProduct) return 'All Products';
    const product = products.find(p => p.id === selectedProduct);
    return product ? product.name : 'Unknown Product';
  };

  // Date filter dialog functions
  const handleOpenDateFilterDialog = () => {
    setTempDateFilter({ ...dateFilter });
    setDateFilterDialogOpen(true);
  };

  const handleCloseDateFilterDialog = () => {
    setDateFilterDialogOpen(false);
    setTempDateFilter({
      type: 'all',
      startDate: '',
      endDate: '',
      specificDate: '',
      expirationFilter: 'all'
    });
  };

  const handleConfirmDateFilterSelection = () => {
    setDateFilter({ ...tempDateFilter });
    setDateFilterDialogOpen(false);
  };

  const getDateFilterLabel = () => {
    switch (dateFilter.type) {
      case 'range':
        if (dateFilter.startDate && dateFilter.endDate) {
          return `${dayjs(dateFilter.startDate + 'T12:00:00').format('M/D/YYYY')} - ${dayjs(dateFilter.endDate + 'T12:00:00').format('M/D/YYYY')}`;
        }
        return 'Date Range';
      case 'specific':
        if (dateFilter.specificDate) {
          return dayjs(dateFilter.specificDate + 'T12:00:00').format('M/D/YYYY');
        }
        return 'Specific Date';
      case 'expiration':
        const expLabels = {
          'all': 'All Items',
          'expired': 'Expired Items',
          'expiring_soon': 'Expiring Soon',
          'expiring_range': dateFilter.startDate && dateFilter.endDate ? 
            `${dayjs(dateFilter.startDate + 'T12:00:00').format('M/D/YYYY')} - ${dayjs(dateFilter.endDate + 'T12:00:00').format('M/D/YYYY')}` : 
            'Expiring in Range',
          'expiring_specific': dateFilter.specificDate ? 
            `${dayjs(dateFilter.specificDate + 'T12:00:00').format('M/D/YYYY')}` : 
            'Expiring on Day',
          'not_expired': 'Not Expired'
        };
        return `Expiration: ${expLabels[dateFilter.expirationFilter] || 'All Items'}`;
      default:
        return 'All Dates';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedProduct('');
    setDateFilter({
      type: 'all',
      startDate: '',
      endDate: '',
      specificDate: '',
      expirationFilter: 'all'
    });
  };


  // List view render
  const renderBatchesList = () => (
    <Fade in timeout={800}>
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          {/* Enhanced Page Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', 
            mb: 5,
            mt: 5,
            p: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '200px',
              height: '200px',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
              zIndex: 0
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: 'info.main',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 }
                  }
                }} />
                <Typography variant="caption" sx={{ 
                  color: 'info.main', 
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontSize: '0.75rem'
                }}>
                  Batch Management
                </Typography>
              </Box>
              
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                fontSize: '1.8rem',
                mb: 1,
                lineHeight: 1.2
              }}>
                Batches
              </Typography>
              
              <Typography variant="body2" sx={{ 
                color: 'text.secondary', 
                fontWeight: 500,
                fontSize: '0.9rem',
                mb: 2.5,
                maxWidth: '400px'
              }}>
                Comprehensive batch tracking from planning to completion with detailed status monitoring
              </Typography>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Chip 
                  label={`${filteredBatches.length} ${filteredBatches.length === 1 ? 'Batch' : 'Batches'}`} 
                  size="small" 
                  sx={{ 
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: 'info.main',
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                  }} 
                />
                <Chip 
                  label={`${filteredBatches.filter(b => b.status === 'completed').length} Completed`} 
                  size="small" 
                  sx={{ 
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: 'success.main',
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                  }} 
                />
                <Chip 
                  label={`${filteredBatches.filter(b => ['in_production', 'quality_testing'].includes(b.status)).length} Active`} 
                  size="small" 
                  sx={{ 
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    color: 'warning.main',
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                  }} 
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', position: 'relative', zIndex: 1 }}>
              {canCreateProductionBatches() && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateNew}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.2,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    bgcolor: 'primary.main',
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      transform: 'translateY(-1px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                    }
                  }}
                >
                  New Batch
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Production Overview Cards */}
        <Box sx={{ 
          mb: 5, 
          px: 1,
          animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@keyframes slideInUp': {
            '0%': {
              transform: 'translateY(100%)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1,
            }
          }
        }}>
        </Box>

        {/* Search and Filter Bar */}
        <Box sx={{ mb: 8 }}>
          <Paper 
            sx={{ 
              p: 4, 
              mb: 5, 
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '@keyframes slideInUp': {
                '0%': {
                  transform: 'translateY(100%)',
                  opacity: 0,
                },
                '100%': {
                  transform: 'translateY(0)',
                  opacity: 1,
                }
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                fontWeight: 500,
                color: 'primary.main',
                fontSize: '1.25rem'
              }}
            >
              Search & Filter Batches
            </Typography>
            
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    color: 'text.secondary',
                    fontSize: '0.9rem'
                  }}>
                    Search
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Search batches..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main }} />
                        </InputAdornment>
                      ),
                      sx: { 
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        height: 56,
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '1rem',
                        fontWeight: 500,
                        py: 2,
                        px: 2
                      },
                      '& .MuiOutlinedInput-root': {
                        height: 56,
                        '& fieldset': {
                          border: 'none'
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none'
                        }
                      }
                    }}
                    size="medium"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    color: 'text.secondary',
                    fontSize: '0.9rem'
                  }}>
                    Product
                  </Typography>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleOpenProductFilterDialog}
                    sx={{
                      justifyContent: 'space-between',
                      textAlign: 'left',
                      height: 56,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      color: 'text.primary',
                      fontWeight: 500,
                      px: 2,
                      textTransform: 'none',
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }}
                    endIcon={<ExpandMoreIcon />}
                  >
                    {getSelectedProductName()}
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    color: 'text.secondary',
                    fontSize: '0.9rem'
                  }}>
                    Date Filter
                  </Typography>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleOpenDateFilterDialog}
                    sx={{
                      justifyContent: 'space-between',
                      textAlign: 'left',
                      height: 56,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      color: 'text.primary',
                      fontWeight: 500,
                      px: 2,
                      textTransform: 'none',
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }}
                    endIcon={<ExpandMoreIcon />}
                  >
                    {getDateFilterLabel()}
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Box sx={{ display: 'flex', gap: 1, height: 56, alignItems: 'center' }}>
                  <Tooltip title="Clear All Filters">
                    <IconButton 
                      onClick={clearFilters}
                      sx={{ 
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        bgcolor: 'white',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.error.main, 0.04),
                          borderColor: theme.palette.error.main,
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.2)}`
                        }
                      }}
                    >
                      <FilterListIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Refresh Data">
                    <IconButton 
                      onClick={fetchBatches}
                      sx={{ 
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        bgcolor: 'white',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                          borderColor: theme.palette.primary.main,
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>



        {/* Batches Content */}
        <Paper 
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '@keyframes slideInUp': {
              '0%': {
                transform: 'translateY(100%)',
                opacity: 0,
              },
              '100%': {
                transform: 'translateY(0)',
                opacity: 1,
              }
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}
        >
          <Box sx={{ bgcolor: 'transparent', p: 4 }}>


                  {/* Batches Table */}
                  <Paper sx={{ 
                    borderRadius: 3, 
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`
                    }
                  }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={48} />
            </Box>
          ) : (
            <TableContainer sx={{ overflow: 'visible' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}>Batch #</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}>Product</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}>Formulation</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}>Quantity</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}>Status</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}>Mfg Date</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}>Expiration</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}>Total Cost</TableCell>
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3,
                      width: 120
                    }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBatches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                        <Stack spacing={2} alignItems="center">
                          <LocalDrinkIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {searchTerm ? 'No batches found matching your search' : 'No production batches yet'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {searchTerm ? 'Try adjusting your search terms' : 'Create your first production batch to get started'}
                          </Typography>
                          {!searchTerm && canCreateProductionBatches() && (
                            <Button
                              variant="contained"
                              startIcon={<AddIcon />}
                              onClick={handleCreateNew}
                              sx={{ 
                                mt: 2,
                                borderRadius: 3,
                                px: 4,
                                py: 1.5,
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                textTransform: 'none',
                                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                '&:hover': {
                                  transform: 'translateY(-1px) scale(1.02)',
                                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
                                }
                              }}
                            >
                              Create First Batch
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : (
                    (filteredBatches.length > 0 
                      ? filteredBatches.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      : []
                    ).map((batch) => (
                      <TableRow 
                        key={batch.id} 
                        hover 
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': { 
                            bgcolor: 'grey.50',
                            transform: 'translateX(4px)',
                            boxShadow: 'inset 4px 0 0 black'
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                            py: 2.5,
                            px: 3
                          }
                        }}
                        onClick={() => handleViewDetails(batch)}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                            {batch.batch_number || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                            {batch.product?.name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                              {batch.formulation?.name || 'N/A'}
                            </Typography>
                            {batch.formulation?.version && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                v{batch.formulation.version}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <BatchQuantityCell batch={batch} />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={formatStatus(batch.status)}
                            color={getStatusColor(batch.status)}
                            size="small"
                            sx={{ 
                              minWidth: 100,
                              fontWeight: 500,
                              borderRadius: 2,
                              fontSize: '0.75rem'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                            {formatDateForDisplay(batch.manufacture_date)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                            {formatDateForDisplay(batch.expiration_date)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem', color: batchCosts[batch.id] ? '#2e7d32' : 'text.secondary' }}>
                            {formatCurrency(batchCosts[batch.id])}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenMenu(e, batch);
                            }}
                            disabled={!canEditPage('production_batches') && !canPermanentlyDeleteProductionBatches()}
                            sx={{ 
                              p: 1,
                              borderRadius: 2,
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                              bgcolor: (canEditPage('production_batches') || canPermanentlyDeleteProductionBatches()) ? 'black' : alpha(theme.palette.grey[400], 0.5),
                              color: (canEditPage('production_batches') || canPermanentlyDeleteProductionBatches()) ? 'white' : alpha(theme.palette.grey[600], 0.7),
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': (canEditPage('production_batches') || canPermanentlyDeleteProductionBatches()) ? { 
                                bgcolor: 'grey.900',
                                color: 'white',
                                transform: 'translateY(-2px) scale(1.05)',
                                boxShadow: `0 6px 18px ${alpha(theme.palette.grey[900], 0.4)}`,
                                border: `1px solid ${theme.palette.grey[900]}`
                              } : {},
                              '&:disabled': {
                                bgcolor: alpha(theme.palette.grey[400], 0.5),
                                color: alpha(theme.palette.grey[600], 0.7),
                                border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`
                              }
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!loading && (
            <Box sx={{ 
              borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
              borderRadius: '0 0 12px 12px'
            }}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredBatches.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ 
                  px: 3,
                  py: 2,
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    color: theme.palette.text.primary
                  },
                  '& .MuiTablePagination-select': {
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    background: 'white',
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    }
                  },
                  '& .MuiTablePagination-actions': {
                    '& .MuiIconButton-root': {
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      borderRadius: 2,
                      margin: '0 2px',
                      background: 'white',
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: theme.palette.primary.main,
                        color: 'white',
                        transform: 'translateY(-1px)',
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      },
                      '&.Mui-disabled': {
                        background: alpha(theme.palette.grey[300], 0.5),
                        color: theme.palette.grey[500],
                        border: `1px solid ${theme.palette.grey[300]}`,
                        transform: 'none',
                        boxShadow: 'none'
                      }
                    }
                  }
                }}
              />
                    </Box>
                  )}
                  </Paper>
          </Box>
        </Paper>

        {/* Context Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseMenu}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`
            }
          }}
        >
          {canEditPage('production_batches') && (
            <MenuItem 
              onClick={handleEditBatch}
              sx={{
                py: 1.5,
                px: 2,
                fontSize: '0.9rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08)
                }
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Batch</ListItemText>
            </MenuItem>
          )}
          {canPermanentlyDeleteProductionBatches() && (
            <MenuItem 
              onClick={handleOpenDeleteDialog} 
              sx={{ 
                color: 'error.main',
                py: 1.5,
                px: 2,
                fontSize: '0.9rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: alpha(theme.palette.error.main, 0.08)
                }
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete Batch</ListItemText>
            </MenuItem>
          )}
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={handleCloseDeleteDialog}
          PaperProps={{
            sx: {
              borderRadius: 4,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontSize: '1.25rem', 
            fontWeight: 500,
            color: 'text.primary'
          }}>
            Delete Production Batch
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
              Are you sure you want to delete the batch "{selectedBatch?.batch_number}"? 
              This action cannot be undone and will remove all associated data including ingredients, 
              equipment usage, costs, and production logs.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={handleCloseDeleteDialog}
              sx={{ 
                borderRadius: 6,
                textTransform: 'none',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteBatch} 
              color="error" 
              variant="contained"
              disabled={deleting}
              sx={{ 
                borderRadius: 6,
                textTransform: 'none',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                boxShadow: `0 6px 18px ${alpha(theme.palette.error.main, 0.25)}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px) scale(1.02)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.error.main, 0.35)}`
                }
              }}
            >
              {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Product Filter Dialog */}
        <Dialog
          open={productFilterDialogOpen}
          onClose={handleCloseProductFilterDialog}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: 4,
              height: '80vh',
              margin: 'auto',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            fontSize: '1.25rem', 
            fontWeight: 500,
            color: 'text.primary',
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
              Select Product
            </Typography>
            <IconButton onClick={handleCloseProductFilterDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
              <TextField
                fullWidth
                placeholder="Search products..."
                value={productFilterSearchTerm}
                onChange={(e) => setProductFilterSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  }
                }}
              />
            </Box>
            
            <Box sx={{ 
              flex: 1,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow 
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2,
                        pl: 3,
                        pr: 3
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          All Products
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Show all batches
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDialogProductFilterSelect('all')}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          bgcolor: dialogSelectedProductFilter === 'all' ? 'black' : 'transparent',
                          color: dialogSelectedProductFilter === 'all' ? 'white' : 'inherit',
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            bgcolor: dialogSelectedProductFilter === 'all' ? 'black' : 'transparent',
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                  {getFilteredProductsForFilter().map((product) => (
                    <TableRow 
                      key={product.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'grey.50',
                          transform: 'translateX(4px)',
                          boxShadow: 'inset 4px 0 0 black'
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                          py: 2,
                          pl: 3,
                          pr: 3
                        }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {product.name}
                          </Typography>
                          {product.sku && (
                            <Typography variant="caption" color="text.secondary">
                              SKU: {product.sku}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleDialogProductFilterSelect(product)}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 1,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            bgcolor: dialogSelectedProductFilter?.id === product.id ? 'black' : 'transparent',
                            color: dialogSelectedProductFilter?.id === product.id ? 'white' : 'inherit',
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            '&:hover': {
                              border: `1px solid ${theme.palette.primary.main}`,
                              bgcolor: dialogSelectedProductFilter?.id === product.id ? 'black' : 'transparent',
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {getFilteredProductsForFilter().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {productFilterSearchTerm ? 'No products found matching your search.' : 'No products available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3, 
            gap: 2, 
            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            bgcolor: alpha(theme.palette.grey[50], 0.5),
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <Button
              onClick={() => window.open(`/company/${companyId}/products`, '_blank')}
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
                color: 'success.main',
                '&:hover': {
                  border: `1px solid ${theme.palette.success.main}`,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Add Product
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                onClick={handleCloseProductFilterDialog}
                variant="outlined"
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  '&:hover': {
                    border: `1px solid ${theme.palette.grey[600]}`,
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmProductFilterSelection}
                variant="contained"
                disabled={!productSelectionMade}
                sx={{ 
                  borderRadius: 2, 
                  px: 3, 
                  py: 1, 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                  },
                  '&:disabled': {
                    background: alpha(theme.palette.grey[400], 0.5),
                    color: alpha(theme.palette.grey[600], 0.7)
                  }
                }}
              >
                Confirm Selection
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Date Filter Dialog */}
        <Dialog
          open={dateFilterDialogOpen}
          onClose={handleCloseDateFilterDialog}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 4,
              width: '600px',
              maxWidth: '600px',
              margin: 'auto',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            fontSize: '1.25rem', 
            fontWeight: 500,
            color: 'text.primary',
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
              Date Filter Options
            </Typography>
            <IconButton onClick={handleCloseDateFilterDialog} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel sx={{ fontWeight: 500 }}>Filter Type</InputLabel>
                  <Select
                    value={tempDateFilter.type}
                    label="Filter Type"
                    onChange={(e) => setTempDateFilter(prev => ({ ...prev, type: e.target.value }))}
                    sx={{
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.grey[400], 0.8)
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  >
                    <MenuItem value="all">All Dates</MenuItem>
                    <MenuItem value="range">Date Range</MenuItem>
                    <MenuItem value="specific">Specific Date</MenuItem>
                    <MenuItem value="expiration">Expiration Filter</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {tempDateFilter.type === 'range' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Start Date"
                        value={tempDateFilter.startDate ? dayjs(tempDateFilter.startDate + 'T12:00:00') : null}
                        onChange={(newValue) => {
                          setTempDateFilter(prev => ({ 
                            ...prev, 
                            startDate: newValue ? newValue.format('YYYY-MM-DD') : ''
                          }));
                        }}
                        format="MM/DD/YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '& fieldset': {
                                  borderColor: alpha(theme.palette.grey[400], 0.8)
                                },
                                '&:hover fieldset': {
                                  borderColor: theme.palette.primary.main
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main
                                }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="End Date"
                        value={tempDateFilter.endDate ? dayjs(tempDateFilter.endDate + 'T12:00:00') : null}
                        onChange={(newValue) => {
                          setTempDateFilter(prev => ({ 
                            ...prev, 
                            endDate: newValue ? newValue.format('YYYY-MM-DD') : ''
                          }));
                        }}
                        format="MM/DD/YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '& fieldset': {
                                  borderColor: alpha(theme.palette.grey[400], 0.8)
                                },
                                '&:hover fieldset': {
                                  borderColor: theme.palette.primary.main
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette.primary.main
                                }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </>
              )}

              {tempDateFilter.type === 'specific' && (
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Specific Date"
                      value={tempDateFilter.specificDate ? dayjs(tempDateFilter.specificDate + 'T12:00:00') : null}
                        onChange={(newValue) => {
                          setTempDateFilter(prev => ({ 
                            ...prev, 
                            specificDate: newValue ? newValue.format('YYYY-MM-DD') : ''
                          }));
                        }}
                        format="MM/DD/YYYY"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: alpha(theme.palette.grey[400], 0.8)
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main
                              }
                            }
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              )}

              {tempDateFilter.type === 'expiration' && (
                <>
                  <Grid item xs={12}>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel sx={{ fontWeight: 500 }}>Expiration Status</InputLabel>
                      <Select
                        value={tempDateFilter.expirationFilter}
                        label="Expiration Status"
                        onChange={(e) => setTempDateFilter(prev => ({ ...prev, expirationFilter: e.target.value }))}
                        sx={{
                          borderRadius: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha(theme.palette.grey[400], 0.8)
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main
                          }
                        }}
                      >
                        <MenuItem value="all">All Items</MenuItem>
                        <MenuItem value="expired">Expired Items</MenuItem>
                        <MenuItem value="expiring_soon">Expiring Soon (30 days)</MenuItem>
                        <MenuItem value="expiring_range">Expiring in Date Range</MenuItem>
                        <MenuItem value="expiring_specific">Expiring on Specific Day</MenuItem>
                        <MenuItem value="not_expired">Not Expired</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {tempDateFilter.expirationFilter === 'expiring_specific' && (
                    <Grid item xs={12}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Expiration Date"
                          value={tempDateFilter.specificDate ? dayjs(tempDateFilter.specificDate + 'T12:00:00') : null}
                          onChange={(newValue) => {
                            setTempDateFilter(prev => ({ 
                              ...prev, 
                              specificDate: newValue ? newValue.format('YYYY-MM-DD') : ''
                            }));
                          }}
                          format="MM/DD/YYYY"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  '& fieldset': {
                                    borderColor: alpha(theme.palette.grey[400], 0.8)
                                  },
                                  '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.primary.main
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  )}
                  
                  {(tempDateFilter.expirationFilter === 'expiring_soon' || tempDateFilter.expirationFilter === 'expiring_range') && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Expiration Start Date"
                            value={tempDateFilter.startDate ? dayjs(tempDateFilter.startDate + 'T12:00:00') : null}
                            onChange={(newValue) => {
                              setTempDateFilter(prev => ({ 
                                ...prev, 
                                startDate: newValue ? newValue.format('YYYY-MM-DD') : ''
                              }));
                            }}
                            format="MM/DD/YYYY"
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                sx: {
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '& fieldset': {
                                      borderColor: alpha(theme.palette.grey[400], 0.8)
                                    },
                                    '&:hover fieldset': {
                                      borderColor: theme.palette.primary.main
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: theme.palette.primary.main
                                    }
                                  }
                                }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Expiration End Date"
                            value={tempDateFilter.endDate ? dayjs(tempDateFilter.endDate + 'T12:00:00') : null}
                            onChange={(newValue) => {
                              setTempDateFilter(prev => ({ 
                                ...prev, 
                                endDate: newValue ? newValue.format('YYYY-MM-DD') : ''
                              }));
                            }}
                            format="MM/DD/YYYY"
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                sx: {
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '& fieldset': {
                                      borderColor: alpha(theme.palette.grey[400], 0.8)
                                    },
                                    '&:hover fieldset': {
                                      borderColor: theme.palette.primary.main
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: theme.palette.primary.main
                                    }
                                  }
                                }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </>
                  )}
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3, 
            gap: 2, 
            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            bgcolor: alpha(theme.palette.grey[50], 0.5),
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            <Button
              onClick={handleCloseDateFilterDialog}
              variant="outlined"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  border: `1px solid ${theme.palette.grey[600]}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDateFilterSelection}
              variant="contained"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}
            >
              Apply Filter
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Fade>
  );

  // Detail view render
  const renderBatchDetail = () => {
    if (!currentBatch) return null;

    return (
      <Container disableGutters maxWidth={false} sx={{ 
        overflow: 'visible', 
        px: 2,
        pl: 3,
        pr: 3,
        maxWidth: '100%',
        width: '100%'
      }}>
        <BatchTabs
          ref={batchTabsRef}
          batch={currentBatch}
          formulations={formulations}
          products={products}
          rawMaterials={rawMaterials}
          showSnackbar={showSnackbar}
          user={user}
          userCompanyId={companyId}
          onUpdate={refreshAllData}
          onBackToList={handleBackToList}
          batchTimeFormat={batchTimeFormat}
        />
      </Container>
    );
  };

  // Batch quantity cell component
  const BatchQuantityCell = ({ batch }) => {
    const formatQuantity = (quantity) => {
      if (!quantity) return 'N/A';
      return parseFloat(quantity).toLocaleString();
    };

    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
          {batch.calculated_production_volume ? 
            `${formatQuantity(batch.calculated_production_volume)} ${batch.calculated_production_unit || 'gal'}` :
            `${formatQuantity(batch.planned_quantity)} ${batch.planned_unit_of_measure}`
          }
        </Typography>
        {batch.calculated_production_volume && batch.planned_unit_of_measure !== (batch.calculated_production_unit || 'gal') && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 500 }}>
            ({formatQuantity(batch.planned_quantity)} {batch.planned_unit_of_measure})
          </Typography>
        )}
        {batch.actual_quantity && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 500 }}>
            Actual: {formatQuantity(batch.actual_quantity)} {batch.planned_unit_of_measure}
          </Typography>
        )}
      </Box>
    );
  };

  if (loading && viewMode === 'list') {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mb: 2
              }}>
                <LocalDrinkIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 500,
              color: 'primary.main',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Batches
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Analyzing batch production and manufacturing data...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'primary.main',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Box>
      {viewMode === 'list' ? (
        <Fade in={viewMode === 'list'} key="list-view" timeout={600}>
          <Box>
            {renderBatchesList()}
          </Box>
        </Fade>
      ) : (
        <Fade in={viewMode === 'detail'} key="detail-view" timeout={600}>
          <Box>
            {renderBatchDetail()}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default BatchingPage; 